/**
 * The top bar: global search, the signed-in user, sign out.
 *
 * 60px, white, and sticky — it stays put while a long list scrolls beneath it,
 * because search is the fastest route to any record and hiding it behind a
 * scroll-to-top makes it slower than the navigation it replaces.
 *
 * The menu button appears below `lg` only, where the rail is not rendered.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.6
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { SessionUser } from '../../lib/auth-types';
import { ROUTES } from '../../lib/routes';
import { OwnerAvatar } from '../domain/OwnerAvatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';

export interface TopBarProps {
  user: SessionUser | null;
  onOpenMenu: () => void;
  menuExpanded: boolean;
  onSignOut: () => void;
}

export function TopBar({
  user,
  onOpenMenu,
  menuExpanded,
  onSignOut,
}: TopBarProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  /* A form rather than a keydown handler: Enter-to-submit then comes from the
     browser, and mobile keyboards show a "search" key instead of a newline. */
  const handleSearch = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed !== '') {
      void navigate(`${ROUTES.search}?q=${encodeURIComponent(trimmed)}`);
    }
  };

  return (
    <header className="border-gray-100 px-s4 gap-s3 sticky top-0 z-20 flex h-[60px] flex-none items-center border-b bg-white">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={t('nav.openMenu')}
        aria-expanded={menuExpanded}
        className="hover:bg-gray-100 -ml-2 grid size-11 flex-none place-items-center rounded-md lg:hidden"
      >
        <Icon name="checklist" />
      </button>

      {/* Hidden below 640px. At 375px the field, the role badge, the avatar,
          the name and Sign out cannot share 60px of width: the search input
          collapses to about three characters, which is worse than absent. The
          search *route* stays reachable from the drawer, so nothing is lost —
          only this shortcut to it. */}
      <form
        onSubmit={handleSearch}
        role="search"
        className="relative hidden max-w-[420px] flex-1 sm:block"
      >
        <label htmlFor="global-search" className="sr-only">
          {t('search.label')}
        </label>
        <Icon
          name="search"
          size="sm"
          className="text-gray-400 pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2"
        />
        <input
          id="global-search"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={t('search.placeholder')}
          className="border-gray-100 bg-off-white focus:border-blue min-h-11 w-full rounded-full border-[1.5px] py-2.5 pr-3.5 pl-9 text-[13px] focus:bg-white focus:outline-none"
        />
      </form>

      <div className="ml-auto flex min-w-0 items-center gap-2.5 text-[13px]">
        {user !== null && (
          <>
            {/* The role is shown because it explains why the interface differs
                between two people looking at the same record. */}
            <Badge tone={user.role === 'ADMIN' ? 'info' : 'mute'}>
              {t(`role.${user.role}`)}
            </Badge>
            <OwnerAvatar name={user.name} />
            <span className="text-body hidden sm:inline">{user.name}</span>
          </>
        )}
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          {t('actions.signOut')}
        </Button>
      </div>
    </header>
  );
}
